import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useCustomersOverviews } from '../../../hooks/requests/use-customers-overviews/use-customers-overviews';
import { VyAutocompleteSelect } from '../../../components/new-design/inputs/VyAutocompleteSelect';
import { Size, SIZES } from '../../../components/types/sizes';
import { SuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { Skeleton } from '@mui/material';
import { useCallback } from 'react';
import { CustomerOverview } from '@vy/api-definition';
import { isNotNullOrUndefined, isString } from '@vy/utils';

type CustomerAutoCompleteProps<FormFields extends FieldValues> = {
  control: Control<FormFields>;
  name: Path<FormFields>;
  disabled?: boolean;
  size?: Extract<Size, typeof SIZES.SMALL | typeof SIZES.MEDIUM>;
  disableClearable?: boolean;
};

const CustomerAutoComplete = <FormFields extends FieldValues>({
  name,
  size,
  control,
  disabled,
  disableClearable,
}: CustomerAutoCompleteProps<FormFields>) => {
  const { data: customerOverviewResponse } = useCustomersOverviews({
    suspense: true,
  });
  const { customers = [] } = customerOverviewResponse ?? {};

  const filterCustomerByName = useCallback((option: CustomerOverview, searchQuery: string) => {
    return option.name.toLowerCase().includes(searchQuery.toLowerCase());
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...fieldProps }, fieldState: { error } }) => (
        <VyAutocompleteSelect
          label=""
          fullWidth
          size={size}
          options={customers}
          disabled={disabled}
          disableClearable={disableClearable}
          placeholder="Search for a customer"
          getOptionLabel={(option) => option.name}
          onOptionSelect={(value) => onChange(isString(value) ? value : value?.id)}
          filterOption={filterCustomerByName}
          optionsKeyExtractor={(option) => option.id}
          value={customers.find((customer) => customer.id === value)}
          error={isNotNullOrUndefined(error)}
          helperText={error?.message}
          {...fieldProps}
        />
      )}
    />
  );
};

const CustomerAutoCompleteWithSuspense = <FormFields extends FieldValues>({
  ...props
}: CustomerAutoCompleteProps<FormFields>) => (
  <SuspenseBoundary
    suspenseProps={{
      fallback: <Skeleton variant="rounded" width="100%" height="47px" />,
    }}
  >
    <CustomerAutoComplete {...props} />
  </SuspenseBoundary>
);

export { CustomerAutoCompleteWithSuspense as CustomerAutoComplete };
